import React, { useState } from 'react';
import { Download, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import Markdown from 'markdown-to-jsx';
import { Button } from '../ui/button';
import { copyToClipboard as copyText } from '../../utils/clipboard';

interface Phrase {
  phrase_number: number;
  phrase_text: string;
  support: string;
  citations: string[];
  reference_numerals: string[];
}

interface ClaimData {
  phrases: Phrase[];
}

interface ClaimChartData {
  [claimKey: string]: ClaimData;
}

interface DocxFileInfo {
  file_id: string;
  s3_key: string;
  filename: string;
  download_url: string;
  generated_at: string;
}

interface ClaimChartDisplayProps {
  result: ClaimChartData | { claim_chart: ClaimChartData; docx_file?: DocxFileInfo };
  theme?: 'light' | 'dark';
  searchTerm?: string;
  showHeader?: boolean;
  onAddToInput?: (text: string) => void;
  showToast?: (message: string) => void;
  includeSupport?: boolean;
  compactButtons?: boolean;
}

const ClaimChartDisplay: React.FC<ClaimChartDisplayProps> = ({
  result,
  theme = 'light',
  searchTerm = '',
  showHeader = true,
  onAddToInput,
  showToast,
  includeSupport,
  compactButtons = false
}) => {
  // Handle both formats: direct ClaimChartData or object with claim_chart and docx_file
  const claimChartData: ClaimChartData = (result && typeof result === 'object' && 'claim_chart' in result) ? result.claim_chart : (result as ClaimChartData);
  const docxFile: DocxFileInfo | undefined = (result && typeof result === 'object' && 'claim_chart' in result) ? result.docx_file : undefined;

  const [expandedClaims, setExpandedClaims] = useState<Set<string>>(new Set(Object.keys(claimChartData || {})));

  const isDark = theme === 'dark';

  // Auto-detect if support should be shown if not explicitly provided
  // Check if any phrase has support data
  const shouldShowSupport = includeSupport !== undefined
    ? includeSupport
    : (() => {
        // Check if any phrase in any claim has support data
        for (const claimData of Object.values(claimChartData || {})) {
          if (claimData.phrases && claimData.phrases.length > 0) {
            const firstPhrase = claimData.phrases[0];
            if (firstPhrase.support && firstPhrase.support.trim().length > 0) {
              return true;
            }
          }
        }
        return false;
      })();

  if (!claimChartData || typeof claimChartData !== 'object' || Object.keys(claimChartData).length === 0) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
        <p>No claim chart data available.</p>
      </div>
    );
  }

  const toggleClaim = (claimKey: string) => {
    const newExpanded = new Set(expandedClaims);
    if (newExpanded.has(claimKey)) {
      newExpanded.delete(claimKey);
    } else {
      newExpanded.add(claimKey);
    }
    setExpandedClaims(newExpanded);
  };

  const exportToCSV = () => {
    // Build CSV header based on whether support is included
    const headers = shouldShowSupport
      ? 'Claim,Phrase #,Phrase Text,Support,Citations,Reference Numerals\n'
      : 'Claim,Phrase #,Phrase Text,Citations,Reference Numerals\n';
    let csv = headers;

    Object.entries(claimChartData).forEach(([claimKey, claimData]) => {
      const claimNumber = parseInt(claimKey.replace('claim_', ''), 10);

      claimData.phrases?.forEach((phrase) => {
        const baseRow = [
          claimNumber,
          phrase.phrase_number,
          `"${(phrase.phrase_text || '').replace(/"/g, '""')}"`,
        ];

        // Add support column if enabled
        if (shouldShowSupport) {
          baseRow.push(`"${(phrase.support || '').replace(/"/g, '""')}"`);
        }

        // Add citations and reference numerals
        baseRow.push(
          `"${(phrase.citations || []).join(', ')}"`,
          `"${(phrase.reference_numerals || []).join(', ')}"`
        );

        csv += baseRow.join(',') + '\n';
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claim_chart_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    if (showToast) {
      showToast('Claim chart exported to CSV');
    }
  };

  const copyToClipboard = async (text: string) => {
    const copied = await copyText(text);
    if (showToast) {
      showToast(copied ? 'Copied to clipboard' : 'Failed to copy to clipboard');
    }
  };

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightSearchTerm = (text: string) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      i % 2 === 1 ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-700">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const applyHighlightToChildren = (children: React.ReactNode): React.ReactNode => {
    if (!searchTerm || !children) return children;

    if (typeof children === 'string') {
      return highlightSearchTerm(children);
    }

    if (Array.isArray(children)) {
      return children.map((child, idx) => (
        <React.Fragment key={idx}>{applyHighlightToChildren(child)}</React.Fragment>
      ));
    }

    if (React.isValidElement(children)) {
      return React.cloneElement(children, {
        children: applyHighlightToChildren(children.props.children)
      });
    }

    return children;
  };

  const renderSupportMarkdown = (support?: string) => {
    if (!support) return null;

    return (
      <Markdown
        options={{
          forceBlock: true,
          overrides: {
            p: {
              component: ({ children }: { children: React.ReactNode }) => (
                <p className="text-sm leading-relaxed">
                  {applyHighlightToChildren(children)}
                </p>
              )
            },
            strong: {
              component: ({ children }: { children: React.ReactNode }) => (
                <strong className="font-semibold">
                  {applyHighlightToChildren(children)}
                </strong>
              )
            }
          }
        }}
      >
        {support}
      </Markdown>
    );
  };

  // Sort claims numerically
  const sortedClaims = Object.entries(claimChartData).sort(([a], [b]) => {
    const numA = parseInt(a.replace('claim_', ''));
    const numB = parseInt(b.replace('claim_', ''));
    return numA - numB;
  });

  return (
    <div className={`space-y-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {showHeader && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">Claim Chart</h3>
            <div className="flex gap-2">
              {docxFile && (
                <Button
                  onClick={() => window.open(docxFile.download_url, '_blank')}
                  variant="outline"
                  size={compactButtons ? "icon" : "sm"}
                  className={compactButtons ? "" : "gap-2"}
                  title="Download DOCX"
                >
                  <FileText className="w-4 h-4" />
                  {!compactButtons && "Download DOCX"}
                </Button>
              )}
              <Button
                onClick={exportToCSV}
                variant="outline"
                size={compactButtons ? "icon" : "sm"}
                className={compactButtons ? "" : "gap-2"}
                title="Export to CSV"
              >
                <Download className="w-4 h-4" />
                {!compactButtons && "Export to CSV"}
              </Button>
            </div>
          </div>
          {docxFile && (
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Generated: {new Date(docxFile.generated_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        {sortedClaims.map(([claimKey, claimData]) => {
          const claimNumber = parseInt(claimKey.replace('claim_', ''), 10);
          const isExpanded = expandedClaims.has(claimKey);

          if (!claimData.phrases || !Array.isArray(claimData.phrases)) {
            return null;
          }

          return (
            <div
              key={claimKey}
              className={`rounded-lg border ${
                isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              {/* Claim Header */}
              <button
                onClick={() => toggleClaim(claimKey)}
                className={`w-full px-4 py-3 flex items-center justify-between ${
                  isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                } rounded-t-lg transition-colors`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                  <h4 className="font-semibold">Claim {claimNumber}</h4>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    ({claimData.phrases.length} phrases)
                  </span>
                </div>
              </button>

              {/* Claim Chart Table */}
              {isExpanded && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={isDark ? 'bg-gray-750' : 'bg-gray-50'}>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium w-16">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium" style={{ width: shouldShowSupport ? '35%' : '100%' }}>
                          Claim Language
                        </th>
                        {shouldShowSupport && (
                          <th className="px-4 py-2 text-left text-xs font-medium" style={{ width: '65%' }}>
                            Support in Specification
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {claimData.phrases.map((phrase) => (
                        <tr
                          key={phrase.phrase_number}
                          className={`${
                            isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                          } transition-colors`}
                        >
                          {/* Phrase Number */}
                          <td className="px-4 py-3 align-top">
                            <span className={`text-sm font-medium ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {phrase.phrase_number}
                            </span>
                          </td>

                          {/* Phrase Text */}
                          <td className="px-4 py-3 align-top">
                            <div className="space-y-1">
                              <p
                                className="text-sm font-medium cursor-pointer hover:underline"
                                onClick={() => copyToClipboard(phrase.phrase_text)}
                                title="Click to copy"
                              >
                                {highlightSearchTerm(phrase.phrase_text)}
                              </p>
                            </div>
                          </td>

                          {/* Support */}
                          {shouldShowSupport && (
                            <td className="px-4 py-3 align-top">
                              <div className="space-y-2">
                                {renderSupportMarkdown(phrase.support)}

                                {phrase.citations && phrase.citations.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    <span className={`text-xs font-medium ${
                                      isDark ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      Citations:
                                    </span>
                                    {phrase.citations.map((citation, idx) => (
                                      <span
                                        key={idx}
                                        className={`inline-block px-2 py-0.5 rounded text-xs ${
                                          isDark
                                            ? 'bg-blue-900 text-blue-200'
                                            : 'bg-blue-100 text-blue-800'
                                        }`}
                                      >
                                        {citation}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {phrase.reference_numerals && phrase.reference_numerals.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    <span className={`text-xs font-medium ${
                                      isDark ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      Ref. Numerals:
                                    </span>
                                    {phrase.reference_numerals.map((numeral, idx) => (
                                      <span
                                        key={idx}
                                        className={`inline-block px-2 py-0.5 rounded text-xs ${
                                          isDark
                                            ? 'bg-purple-900 text-purple-200'
                                            : 'bg-purple-100 text-purple-800'
                                        }`}
                                      >
                                        {numeral}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sortedClaims.length === 0 && (
        <div className={`p-8 text-center rounded-lg ${
          isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'
        }`}>
          <p>No claims found in the chart data.</p>
        </div>
      )}
    </div>
  );
};

export default ClaimChartDisplay;
